import { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { projects, alsoBuilt, upcomingProjects } from '../data/projects'
import { trackViewContent } from '../lib/tiktokPixel'
import { duration, stagger } from '../lib/motion'

// Lazy-loaded, and only actually mounted once a study is picked (see the
// conditional render at the bottom of this file) — the case-study modal
// is a large chunk of markup and images that's never part of what a
// visitor sees on page load, so there's no reason to make everyone
// download and parse its code up front just because it's somewhere on
// the homepage.
import CaseStudy from './CaseStudy'

// Ambient, silent, looping preview clip used on both case-study cards and
// "Also Built" cards. Gated behind actual viewport visibility rather than
// mounting (and autoplaying) the instant the page loads — with up to 10 of
// these on one page, all autoplaying unconditionally on load was the
// single biggest contributor to this page's mobile performance score:
// several videos fighting for bandwidth and main-thread decode time
// before a visitor had scrolled past the hero. Now each clip only starts
// downloading once its card is about to enter view.
function AmbientPreviewVideo({ src }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '200px' })
  return (
    <div
      ref={ref}
      className="shrink-0 mx-auto sm:mx-0 w-[120px] rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--border)', aspectRatio: '9 / 19.5', background: 'var(--bg-elevated)' }}
    >
      {isInView && (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-auto block pointer-events-none"
        />
      )}
    </div>
  )
}

function TransformationCard({ project, index, onOpen }) {
  const flagship = index === 0
  const previewVideo = project.caseStudy?.previewVideos?.[0]?.src

  return (
    <motion.button
      onClick={() => onOpen(project)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: duration.base, delay: index * stagger.base }}
      whileHover={{ y: -3 }}
      className={`group w-full text-left rounded-2xl border overflow-hidden transition-colors ${
        flagship ? 'md:p-12 p-8' : 'p-8 md:p-10'
      }`}
      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--border-strong)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
    >
      <div className={`flex ${previewVideo ? 'flex-col sm:flex-row' : ''} items-start gap-6`}>
        {/* Same ambient-preview treatment as the "Also Built" cards below —
            no controls (this whole card is already the click target to
            open the case study), just a quiet loop that shows the real
            live site is moving, not a static screenshot. */}
        {previewVideo && <AmbientPreviewVideo src={previewVideo} />}

        <div className="flex-1 flex items-start justify-between gap-6 w-full">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <p className="eyebrow">{project.industry}</p>
              {flagship && (
                <span
                  className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
                >
                  Flagship · Most Recent
                </span>
              )}
            </div>
            <h3
              className={`font-display font-medium ${flagship ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'}`}
              style={{ color: 'var(--text-primary)' }}
            >
              {project.name}
            </h3>
            <p className={`mt-3 max-w-xl ${flagship ? 'text-base' : 'text-sm'}`} style={{ color: 'var(--text-secondary)' }}>
              {project.problem}
            </p>
          </div>
          <span
            className="shrink-0 h-9 w-9 rounded-full border flex items-center justify-center text-sm transition-transform group-hover:translate-x-1"
            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
          >
            ↗
          </span>
        </div>
      </div>

      {project.caseStudy && (
        <p
          className="mt-6 text-sm font-medium underline underline-offset-4 inline-block"
          style={{ color: 'var(--accent)' }}
        >
          Read the case study
        </p>
      )}
    </motion.button>
  )
}

export default function Transformations() {
  const [activeStudy, setActiveStudy] = useState(null)
  const [activeFilter, setActiveFilter] = useState('All')
  const [showAll, setShowAll] = useState(false)

  const industries = ['All', ...new Set(projects.map((p) => p.industry))]
  const filtered = activeFilter === 'All' ? projects : projects.filter((p) => p.industry === activeFilter)
  // Isimi Haven (index 0 of the full, unfiltered list) keeps its Flagship
  // badge only when it's actually showing — filtering it out shouldn't
  // silently hand that badge to whatever's now first.
  const visible = showAll ? filtered : filtered.slice(0, 4)

  return (
    <section id="transformations" className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw">
        <div className="max-w-xl mb-14">
          <p className="eyebrow mb-4">Not a portfolio. Proof.</p>
          <h2 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--text-primary)' }}>
            Real businesses. Real transformations.
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-x-10 gap-y-4 mb-14">
          {projects.map((p, i) => (
            <motion.span
              key={p.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: duration.base, delay: i * stagger.tight }}
              className="font-display text-base md:text-lg"
              style={{ color: 'var(--text-muted)' }}
            >
              {p.name}
            </motion.span>
          ))}
          {upcomingProjects.map((p) => (
            <span
              key={p.id}
              className="font-display text-base md:text-lg"
              style={{ color: 'var(--text-muted)' }}
            >
              {p.label}
            </span>
          ))}
        </div>

        {/* Industry filter — with 9 full case studies now, scrolling
            through all of them unfiltered asks too much of a visitor who
            only cares about one kind of business. Kept as an in-page
            filter rather than separate URLs per category, so this
            content stays on one indexable page instead of fragmenting
            into several thin ones. */}
        <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Filter case studies by industry">
          {industries.map((ind) => (
            <button
              key={ind}
              onClick={() => {
                setActiveFilter(ind)
                setShowAll(false)
              }}
              className="text-xs font-mono tracking-wide px-3 py-1.5 rounded-full border transition-colors text-center leading-snug"
              style={
                activeFilter === ind
                  ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--accent-text)' }
                  : { borderColor: 'var(--border)', color: 'var(--text-secondary)' }
              }
            >
              {ind}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-6">
          {visible.map((p) => (
            <TransformationCard
              key={p.id}
              project={p}
              index={projects.indexOf(p)}
              onOpen={(project) => {
                if (!project.caseStudy) return
                setActiveStudy(project.caseStudy)
                trackViewContent({ id: project.id, name: project.caseStudy.name })
              }}
            />
          ))}
        </div>

        {!showAll && filtered.length > 4 && (
          <button
            onClick={() => setShowAll(true)}
            className="mt-6 text-sm font-medium underline underline-offset-4"
            style={{ color: 'var(--accent)' }}
          >
            Show {filtered.length - 4} more {activeFilter === 'All' ? 'case studies' : `in ${activeFilter}`} →
          </button>
        )}

        {/* Also Built: a deliberately separate category, not more case
            studies — sites we built that don't have a full written case
            study yet. Given its own heading + a top border and real
            breathing room above it (not just mt-6, same as the "Show
            more" link above), so it reads as its own section instead of
            looking like the case study list kept going after the link. */}
        <div className="mt-20 pt-16 border-t" style={{ borderColor: 'var(--border)' }}>
          <p className="eyebrow mb-6">Also Built</p>
          <div className="flex flex-col gap-4">
            {alsoBuilt.map((item, i) => (
            <motion.a
              key={item.id}
              href={`https://${item.url}`}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: duration.base, delay: (projects.length + i) * 0.08 }}
              whileHover={{ y: -3 }}
              className="group w-full text-left rounded-2xl border overflow-hidden transition-colors p-8 md:p-10 block"
              style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--border-strong)')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            >
              <div className={`flex ${item.previewVideo ? 'flex-col sm:flex-row' : ''} items-start gap-6`}>
                {/* Optional lightweight video preview — no controls, purely
                    ambient (this whole card is already a link, so an
                    interactive video with its own controls nested inside
                    an <a> would fight the card's own click target). Used
                    for projects that have a real walkthrough recording but
                    no full case study yet — see data/projects.js. */}
                {item.previewVideo && <AmbientPreviewVideo src={item.previewVideo} />}

                <div className="flex-1 flex items-start justify-between gap-6 w-full">
                  <div>
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <p className="eyebrow">{item.tag}</p>
                      <span
                        className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--border)', color: 'var(--text-secondary)' }}
                      >
                        Also Built
                      </span>
                    </div>
                    <h3 className="font-display font-medium text-2xl md:text-3xl" style={{ color: 'var(--text-primary)' }}>
                      {item.name}
                    </h3>
                    <p className="mt-3 max-w-xl text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {item.description}
                    </p>
                  </div>
                  <span
                    className="shrink-0 h-9 w-9 rounded-full border flex items-center justify-center text-sm transition-transform group-hover:translate-x-1"
                    style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                  >
                    ↗
                  </span>
                </div>
              </div>

              <p
                className="mt-6 text-sm font-medium underline underline-offset-4 inline-block"
                style={{ color: 'var(--accent)' }}
              >
                View site
              </p>
            </motion.a>
          ))}
          </div>
        </div>
      </div>

      {activeStudy && <CaseStudy study={activeStudy} onClose={() => setActiveStudy(null)} />}
    </section>

  )
}
